/**
 * Bearer auth for MapLibre requests to our own API tiles.
 *
 * MapLibre fetches tiles itself, outside axios, so the session token is added in the map's
 * synchronous `transformRequest`. The token store and its Supabase subscription are the
 * raster ones (lib/raster-api.js: `startTileAuth`, `getTileAuthToken`); this module adds
 * the vector-tile URLs (docs/VECTOR_TILES.md) and composes one `mapTransformRequest` for
 * SurveyMap. Only our API's tile URLs get the header; basemaps, third-party services and
 * signed Storage URLs (PMTiles archives) never do.
 */
import { apiBaseUrl } from './portal';
import { getTileAuthToken, rasterTransformRequest, startTileAuth } from './raster-api';

export { startTileAuth };

const VECTOR_TILE_PATH = /^\/api\/datasets\/[^/]+\/tiles\/[a-z_]+\/\d+\/\d+\/\d+\.pbf(\?|$)/;

/** True for a vector tile URL served by `base` (the API origin + path prefix; '' = same origin). */
export function isVectorTileUrl(url, base = apiBaseUrl()) {
  if (typeof url !== 'string') return false;
  let path = url;
  if (base) {
    if (!url.startsWith(`${base}/`)) return false;
    path = url.slice(base.length);
  } else if (/^[a-z]+:\/\//i.test(url)) {
    if (typeof window === 'undefined' || !url.startsWith(`${window.location.origin}/`)) return false;
    path = url.slice(window.location.origin.length);
  }
  return VECTOR_TILE_PATH.test(path);
}

/** transformRequest(url, resourceType) for vector tiles only. */
export function makeVectorTileTransformRequest({ base, getToken = getTileAuthToken } = {}) {
  return (url, resourceType) => {
    if (resourceType && resourceType !== 'Tile') return undefined;
    if (!isVectorTileUrl(url, base === undefined ? apiBaseUrl() : base)) return undefined;
    const token = getToken();
    return token ? { url, headers: { Authorization: `Bearer ${token}` } } : undefined;
  };
}

export const vectorTileTransformRequest = makeVectorTileTransformRequest();

/** SurveyMap's transformRequest: raster tiles (lib/raster-api.js) and vector tiles. */
export function mapTransformRequest(url, resourceType) {
  return rasterTransformRequest(url, resourceType) || vectorTileTransformRequest(url, resourceType);
}
